import React from 'react';
import PropTypes from 'prop-types';
import { NavLink, Link } from 'react-router-dom';
import {
  HiCalculator, HiTrendingUp, HiGlobeAlt, HiBookOpen, HiUserGroup, HiCurrencyDollar, HiScale,
  HiLogout, HiChevronLeft, HiChevronRight,
} from 'react-icons/hi';
import { HiSun, HiMoon } from 'react-icons/hi';
import { useTheme } from '../../contexts/ThemeContext';
import { atende, useAcesso } from '../../contexts/AcessoContext';
import { EXIGE } from '../Acessos/RotaProtegida';
import './Sidebar.css';

/**
 * Menu na ordem da decisão (spec 20261019-refatoracao-navegacao, RF01): o Simulador é o
 * início; Análises é o detalhe por trás dele. O subtítulo diz de onde cada página veio
 * enquanto os nomes antigos ainda estão na cabeça de quem usa.
 */
const ITENS = [
  { to: '/simulador', icone: HiCalculator, rotulo: 'Simulador', sub: 'início · decisão de compra', exige: EXIGE.simulador },
  { to: '/analises', icone: HiTrendingUp, rotulo: 'Análises', sub: 'antigo Alavancagem', exige: EXIGE.analises },
  { to: '/fluxo-caixa', icone: HiCurrencyDollar, rotulo: 'Fluxo de Caixa', sub: 'Projeção', exige: EXIGE.fluxoCaixa },
  { to: '/orcamento', icone: HiScale, rotulo: 'Orçamento', sub: 'orçado × realizado', exige: EXIGE.orcamento },
  { to: '/mercado', icone: HiGlobeAlt, rotulo: 'Mercado', sub: 'antigo Indicadores', exige: EXIGE.mercado },
  { to: '/metodologia', icone: HiBookOpen, rotulo: 'Metodologia', sub: 'fórmulas e dicionário', exige: EXIGE.metodologia },
  { to: '/acessos', icone: HiUserGroup, rotulo: 'Acessos', sub: 'usuários, grupos e roles', exige: EXIGE.acessos },
];

const ItemMenu = ({ item, isCollapsed, className }) => {
  const Icone = item.icone;
  return (
    <li className={`nav-item${className ? ` ${className}` : ''}`}>
      <NavLink
        to={item.to}
        className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
        title={`${item.rotulo} — ${item.sub}`}
      >
        <span className="nav-icon">
          <Icone />
        </span>
        {!isCollapsed && (
          <span className="nav-text">
            {item.rotulo}
            <small className="nav-subtext">{item.sub}</small>
          </span>
        )}
      </NavLink>
    </li>
  );
};

ItemMenu.propTypes = {
  item: PropTypes.shape({
    to: PropTypes.string.isRequired,
    icone: PropTypes.elementType.isRequired,
    rotulo: PropTypes.string.isRequired,
    sub: PropTypes.string.isRequired,
  }).isRequired,
  isCollapsed: PropTypes.bool.isRequired,
  className: PropTypes.string,
};

/** Quem está usando: nome e o que define o acesso (roles e recorte) — some sem identidade. */
const Usuario = ({ isCollapsed }) => {
  const { autenticado, usuario, roles, recorte } = useAcesso();
  if (!autenticado || !usuario || isCollapsed) return null;
  const alcance = recorte?.redeInteira ? 'rede inteira' : (recorte?.ufs || []).join(', ');
  return (
    <div className="sidebar-usuario" title={usuario.email}>
      <span className="sidebar-usuario__nome">{usuario.nome || usuario.email}</span>
      <small className="sidebar-usuario__acesso">
        {roles.length ? roles.join(', ') : 'sem role'}{alcance ? ` · ${alcance}` : ''}
      </small>
    </div>
  );
};

Usuario.propTypes = { isCollapsed: PropTypes.bool.isRequired };

function Sidebar({ onLogout, isCollapsed, toggleSidebar }) {
  const { isDark, toggleTheme } = useTheme();
  const { pode } = useAcesso();
  // O que a role não permite some do menu (RF10); fora do modo `aplicar`, tudo aparece
  const itens = ITENS.filter((item) => atende(pode, item.exige));

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Toggle Button */}
      <button onClick={toggleSidebar} className="sidebar-toggle-btn" aria-label="Expandir ou recolher menu lateral">
        {isCollapsed ? <HiChevronRight /> : <HiChevronLeft />}
      </button>

      {/* Logo Section */}
      <div className="sidebar-header">
        <Link to="/" className="logo-link">
          {isCollapsed ? (
            <img src="/icon-redeancora.png" alt="Rede Ancora" className="logo-collapsed-icon" />
          ) : (
            <img src="/logo_redeancora.png" alt="Logo Rede Ancora" className="sidebar-logo" />
          )}
        </Link>
      </div>

      {/* Navigation Menu */}
      <nav className="sidebar-nav" aria-label="Menu principal">
        <ul className="nav-list">
          {itens.map((item) => (
            <ItemMenu key={item.to} item={item} isCollapsed={isCollapsed} />
          ))}
        </ul>
      </nav>

      {/* Footer with Theme Toggle + Logout */}
      <div className="sidebar-footer">
        <Usuario isCollapsed={isCollapsed} />
        <button
          onClick={toggleTheme}
          className="theme-toggle-btn"
          title={isDark ? 'Modo claro' : 'Modo escuro'}
          aria-label={isDark ? 'Ativar modo claro' : 'Ativar modo escuro'}
        >
          <span className="nav-icon">
            {isDark ? <HiSun /> : <HiMoon />}
          </span>
          {!isCollapsed && <span className="nav-text">{isDark ? 'Modo Claro' : 'Modo Escuro'}</span>}
        </button>
        <button onClick={onLogout} className="logout-btn" title="Sair">
          <span className="nav-icon">
            <HiLogout />
          </span>
          {!isCollapsed && <span className="nav-text">Sair</span>}
        </button>
      </div>
    </div>
  );
}

Sidebar.propTypes = {
  onLogout: PropTypes.func.isRequired,
  isCollapsed: PropTypes.bool.isRequired,
  toggleSidebar: PropTypes.func.isRequired,
};

export default Sidebar;
